// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  DashboardRoute,
  PAGE_ACCESS_OPTIONS,
  ROUTES,
  ROUTE_DEFINITIONS,
  getPath,
} from "../../routes/routes";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { IntlViewerContext, init } from "fbt";
import React, { Suspense, useEffect } from "react";
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";
import { isSignedIn, useAuthState } from "../../store/auth";
import { loadConfig, useConfigState } from "../../store/config";

import { CustomerConfig } from "utils/ConfigTypes";
import CustomerThemeProvider from "style/CustomerThemeProvider";
import Loader from "../ui/Loader";
import NavBar from "../Navigation/NavBar";
import OrgSelection from "components/OrgSelection/OrgSelection";
import PageNotFound from "../PageNotFound/PageNotFound";
import ScrollToTop from "react-router-scroll-top";
import getBrowserLocale from "utils/getBrowserLocale";
import intl from "../../translatedFbts.json";
import { useAppDispatch } from "store";

init({ translations: intl });

// Change your browser locale to French for test on the login page.
IntlViewerContext.locale = getBrowserLocale();

export const CustomerContext = React.createContext<CustomerConfig>(
  {} as CustomerConfig
);

const DashboardApp = () => {
  const authState = useAuthState();
  const { auth, loading: authLoading } = authState;
  const { config } = useConfigState();
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(loadConfig());
  }, [dispatch]);
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop>
          <Switch>
            {ROUTES.map((route: DashboardRoute) => (
              <Route
                exact={!!route.exact}
                key={route.path}
                path={getPath(route, {
                  customerPrefix: ":customerPrefix",
                  asRoute: true,
                })}
                render={({ location, match }) => {
                  if (authLoading || !config) {
                    return <Helmet title={`Loading`} />;
                  }
                  const customerConfig = config.customerConfigs.find(
                    customerConfig =>
                      customerConfig.pathPrefix === match.params.customerPrefix
                  );
                  if (!customerConfig) {
                    if (route.path === "/") {
                      return (
                        <CustomerThemeProvider>
                          <Helmet>
                            <title>OpenRDT</title>
                          </Helmet>
                          <OrgSelection
                            customerConfigs={config.customerConfigs}
                          />
                        </CustomerThemeProvider>
                      );
                    } else {
                      return (
                        <CustomerThemeProvider>
                          {!route.hideNavBar && <NavBar />}
                          <Helmet>
                            <title>{route.seo.title}</title>
                            <meta
                              name="description"
                              content={route.seo.description}
                            />
                          </Helmet>
                          <route.component />
                        </CustomerThemeProvider>
                      );
                    }
                  }
                  const customRouteTitle = customerConfig.customRouteTitle
                    ? customerConfig.customRouteTitle[route.name]
                    : null;
                  if (
                    route.customers &&
                    !route.customers.includes(customerConfig.id)
                  ) {
                    return <PageNotFound />;
                  }
                  if (
                    PAGE_ACCESS_OPTIONS[route.pageAccess]({
                      authState,
                      customerConfig,
                    })
                  ) {
                    return (
                      <CustomerContext.Provider value={customerConfig}>
                        <CustomerThemeProvider>
                          {!route.hideNavBar && <NavBar />}
                          <Helmet>
                            {customRouteTitle ? (
                              <title>{customRouteTitle}</title>
                            ) : (
                              <title>{`${customerConfig?.name}: ${route.seo.title}`}</title>
                            )}
                            <meta
                              name="description"
                              content={route.seo.description}
                            />
                          </Helmet>
                          <Suspense fallback={<Loader />}>
                            <route.component />
                          </Suspense>
                        </CustomerThemeProvider>
                      </CustomerContext.Provider>
                    );
                  }

                  const isAuthenticated = auth && !auth.isGuest;

                  const isEmailVerified =
                    isAuthenticated && auth?.emailVerified;

                  let loggedInRoute =
                    isEmailVerified || customerConfig?.disableLogin
                      ? customerConfig.landingPage
                      : "VERIFYEMAIL";

                  const isUserInOrg = auth?.orgId === customerConfig.id;

                  if (isSignedIn() && !isUserInOrg && isEmailVerified) {
                    loggedInRoute = "NOTINORG";
                  }

                  return (
                    <Redirect
                      to={{
                        pathname: getPath(
                          ROUTE_DEFINITIONS[
                            isAuthenticated ? loggedInRoute : "LOGIN"
                          ]
                        ),
                        state: { from: location },
                      }}
                    />
                  );
                }}
              />
            ))}
            <Route key="pagenotfound">
              <CustomerThemeProvider>
                <Helmet title={`Open RDT: Page not found`} />
                <PageNotFound />
              </CustomerThemeProvider>
            </Route>
          </Switch>
        </ScrollToTop>
      </Router>
    </HelmetProvider>
  );
};

export default DashboardApp;
